import pickBy from 'lodash/pickBy';
import { CAC_TYPE, DISEASES_GRAVITY_VALUES, USER_ROLES } from '../res/Constants';

const uuidv4 = require('uuid/v4');

export const roleLabels = {};
roleLabels[USER_ROLES.admin] = 'Admin';
roleLabels[USER_ROLES.tech] = 'Tech';
roleLabels[USER_ROLES.commercial] = 'Commercial';
roleLabels[USER_ROLES.financial] = 'Financial';
roleLabels[USER_ROLES.client] = 'Customer';

export function removeNullProperties(object) {
    const safeObjectCopy = { ...object };
    Object.entries(safeObjectCopy).forEach(([key, value]) => {
        if (value == null) {
            delete safeObjectCopy[key];
        }
    });
    return safeObjectCopy;
}

/**
 * Removes nested objects
 * @param {object}
 */
export const removeNestedObject = (object) => pickBy(object, (value) => typeof value !== 'object');

// [a-zA-Z] starting letter required by HTML rules for ids
export const randomId = () => `a${uuidv4()}`.replace(/-/g, '');

export const isTrue = (value) => value === true || value === 'true';

/**
 * Determines the line types based on the context and harvest type
 * Returns the actual line values that should be saved to the database
 * @param {number} context - The context type (CAC_TYPE.CROP or CAC_TYPE.NURSERY)
 * @param {string} harvestType - The harvest type (S, M, F, X, Y)
 * @returns {Array<string>} Array of line names
 */
export const getHarvestLines = (context, harvestType) => {
    if (context === CAC_TYPE.CROP) {
        // CROP case
        if (harvestType === 'S') {
            return ['OP'];
        }
        // For M, F, X, Y all return F & M
        if (['M', 'F', 'X', 'Y'].includes(harvestType)) {
            return ['F', 'M'];
        }
    } else if (context === CAC_TYPE.NURSERY) {
        // NURSERY case
        if (harvestType === 'S') {
            return ['OP'];
        }
        if (harvestType === 'M') {
            return ['M'];
        }
        if (harvestType === 'F') {
            return ['F'];
        }
    }

    // Default case if no match
    return [];
};

// ============================================
// Multi-select filter helpers
// ============================================

/**
 * Sanitize empty filter value to null
 * @param {string} value - The filter value
 * @returns {string|null} The sanitized value
 */
export const sanitizeEmptyFilter = (value) => (value === '' ? null : value);

/**
 * Convert comma-separated filter values to array of indices
 * @param {Array} filterValues - Array of filter objects with 'name' property
 * @param {string} prevFilterValue - Comma-separated string of selected values
 * @returns {Array<number>} Array of indices
 */
export const getMultiSelectIndices = (filterValues, prevFilterValue) => {
    if (!prevFilterValue || !filterValues) return [];
    const selectedNames = prevFilterValue.split(',').map(v => v.trim());
    return selectedNames
        .map(name => filterValues.findIndex(item => item.name === name))
        .filter(index => index !== -1);
};

/**
 * Convert comma-separated filter values to array of names
 * @param {string} prevFilterValue - Comma-separated string of selected values
 * @returns {Array<string>} Array of names
 */
export const getMultiSelectValues = (prevFilterValue) => {
    if (!prevFilterValue) return [];
    return prevFilterValue.split(',').map(v => v.trim());
};

/**
 * Convert comma-separated values (codes) to names for display
 * @param {Array} filterValues - Array of filter objects with 'value' and 'name' properties
 * @param {string} prevFilterValue - Comma-separated string of codes
 * @returns {Array<string>} Array of names
 */
export const getMultiSelectNamesFromValues = (filterValues, prevFilterValue) => {
    if (!prevFilterValue || !filterValues) return [];
    const codes = prevFilterValue.split(',').map(v => v.trim());
    return codes
        .map(code => filterValues.find(item => item.value === code)?.name)
        .filter(Boolean);
};

/**
 * Convert comma-separated names to values (codes) for API
 * @param {Array} filterValues - Array of filter objects with 'value' and 'name' properties
 * @param {string} namesString - Comma-separated string of names
 * @returns {string|null} Comma-separated string of codes or null
 */
export const convertNamesToValues = (filterValues, namesString) => {
    if (!namesString || !filterValues) return null;
    const names = namesString.split(',').map(v => v.trim());
    const values = names
        .map(name => filterValues.find(item => item.name === name)?.value)
        .filter(Boolean);
    return values.length === 0 ? null : values.join(',');
};

/**
 * Format multi-select display text: "First + N"
 * @param {Array<string>} values - Array of selected values
 * @param {string} fallbackLabel - Label to show when nothing is selected
 * @returns {string} Formatted display text
 */
export const formatMultiSelectText = (values, fallbackLabel) => {
    if (!values || values.length === 0) return fallbackLabel;
    if (values.length === 1) return values[0];
    return `${values[0]} + ${values.length - 1}`;
};

// ============================================
// Dropdown selection helpers
// ============================================

/**
 * Handle dropdown item click for both single and multi-select modes
 * @param {Object} params - Parameters object
 * @param {number|null} params.index - The clicked item index (null to clear)
 * @param {Array} params.values - Array of value objects with 'value' and 'name' properties
 * @param {boolean} params.multiSelect - Whether multi-select is enabled
 * @param {Array<string>} params.selectedValues - Current selected values (for multi-select)
 * @param {Function} params.onChangeValue - Callback when value changes
 * @returns {Object} New state: { selectedValue, selectedValues, resultValue }
 */
export const handleDropdownSelection = ({ index, values, multiSelect, selectedValues, onChangeValue }) => {
    if (multiSelect) {
        // Multi-select mode
        if (index == null) {
            onChangeValue?.(null);
            return { selectedValues: [], resultValue: null };
        }

        const { value, name } = values[index];
        const itemValue = name ?? value;
        const isSelected = selectedValues.includes(itemValue);

        let newSelectedValues;
        if (isSelected) {
            newSelectedValues = selectedValues.filter(v => v !== itemValue);
        } else {
            newSelectedValues = [...selectedValues, itemValue];
        }

        const resultValue = newSelectedValues.length === 0 ? null : newSelectedValues.join(',');
        onChangeValue?.(resultValue);

        return { selectedValues: newSelectedValues, resultValue };
    } else {
        // Single-select mode
        if (index == null) {
            onChangeValue?.(null);
            return { selectedValue: null, resultValue: null };
        }

        const { value, name } = values[index];
        const newValue = name ?? value;
        onChangeValue?.(newValue);

        return { selectedValue: newValue, resultValue: newValue };
    }
};

/**
 * Calculate display props for dropdown (text, badge count, hasEmptyValue)
 * @param {Object} params - Parameters object
 * @param {boolean} params.multiSelect - Whether multi-select is enabled
 * @param {string} params.selectedValue - Current selected value (for single-select)
 * @param {Array<string>} params.selectedValues - Current selected values (for multi-select)
 * @param {string} params.defaultValue - Default display text
 * @param {boolean} params.hasEmptyValueProp - The hasEmptyValue prop value
 * @returns {Object} Display props: { text, badgeCount, hasEmptyValue }
 */
export const getDropdownDisplayProps = ({ multiSelect, selectedValue, selectedValues, defaultValue, hasEmptyValueProp }) => {
    let text;
    let badgeCount = 0;

    if (multiSelect) {
        const count = selectedValues.length;
        if (count === 0) {
            text = defaultValue;
        } else {
            text = selectedValues[0];
            badgeCount = count - 1;
        }
    } else {
        text = selectedValue || defaultValue;
    }

    // For multi-select, show empty option only when there are selections (to allow clear)
    // For single-select, use the prop value
    const hasEmptyValue = multiSelect
        ? selectedValues.length > 0
        : hasEmptyValueProp;

    return { text, badgeCount, hasEmptyValue };
};

export const getDiseasesGravityLabel = (value) => {
    const found = DISEASES_GRAVITY_VALUES.find(item => item.value == value);
    return found ? found.label : value;
};